import { log } from 'log';
import { difference } from 'ramda';
import { CATEGORY_TYPES, TGamesFeedCategory } from 'js/hooks/games/types';
import { TPlayerTag } from 'js/hooks/socket/hooks/useGetPlayerTags/types';
import { getSegmentationPlayerTags, isShownByLogin, isShownByScheduler, isShownBySegmentation } from 'js/utils/segmentation/segmentation.util';

import * as Types from './types';

export const filterCategories = async ({
    os,
    lang,
    country,
    isLoggedIn,
    formFactor,
    browserName,
    urlTemplates,
    getPlayerTags,
    gamesFeedCategories,
    categoryNameURLParam = ''
}: Types.IFilterCategoriesArguments): Promise<{
    filteredCategories: TGamesFeedCategory[];
    categoriesInfo: Types.ICategoryInfo;
}> => {
    let playerTags: TPlayerTag[] = [];
    let playerTagsToRequest: string[] = [];
    let filteredCategories: TGamesFeedCategory[] = [];
    const categoriesInfo: Types.ICategoryInfo = {
        isRecentlyPlayedCategoryPresent: false,
        pggCategories: []
    };

    // basic category filtration
    filteredCategories = gamesFeedCategories.filter((category) => {
        const {
            scheduler,
            isEnabledForGuest,
            schedulerTimeZone,
            loggedInSegmentation,
            isEnabledForLoggedIn,
            categoryConfiguration,
            seoFriendlyCategoryName
        } = category;

        const isShownByCategoryNameURLParam = !categoryNameURLParam ||
            categoryNameURLParam && categoryNameURLParam === seoFriendlyCategoryName;

        const isCategoryShown =
            categoryConfiguration.isActive &&
            isShownByCategoryNameURLParam &&
            isShownByLogin({ isEnabledForGuest, isEnabledForLoggedIn, isLoggedIn }) &&
            isShownByScheduler({ scheduler, schedulerTimeZone });

        if (isCategoryShown) {
            // gather player tags for segmentation filtration
            playerTagsToRequest = playerTagsToRequest.concat(
                difference(getSegmentationPlayerTags(loggedInSegmentation), playerTagsToRequest)
            );
        }

        return isCategoryShown;
    });

    // requesting player tags for segmentation filtration
    if (playerTagsToRequest.length > 0 && isLoggedIn) {
        try {
            const { tags } = await getPlayerTags({ tagNames: playerTagsToRequest });

            playerTags = tags;
        } catch (error) {
            log('error', '[Games Grid] Unable to get segmentation player tags', error);
        }
    }

    // segmentation filtration
    filteredCategories = filteredCategories.filter((category) => {
        const { loggedInSegmentation, name, guestSegmentation, categoryConfiguration } = category;
        const { categoryType } = categoryConfiguration;

        const isCategoryShown = isShownBySegmentation({ guestSegmentation, loggedInSegmentation, options: {
            os,
            isLoggedIn,
            playerTags,
            formFactor,
            browserName,
            userLanguage: lang,
            userCountry: country,
            cookies: document.cookie,
            segmentationURLTemplates: urlTemplates,
            urlSearchParams: Object.fromEntries(new URLSearchParams(location.search))
        } });

        if (isCategoryShown) {
            categoriesInfo.isRecentlyPlayedCategoryPresent =
                categoryType === CATEGORY_TYPES.recent || categoriesInfo.isRecentlyPlayedCategoryPresent;

            if (categoryType === CATEGORY_TYPES.pgg) {
                categoriesInfo.pggCategories.push({
                    categoryName: name,
                    pggCategoryName: categoryConfiguration.pggCategoryName,
                    maxGames: categoryConfiguration.pggMaxGames
                });
            }
        }

        return isCategoryShown;
    });

    return { filteredCategories, categoriesInfo };
};
